import React, { useState, useEffect } from 'react';
import { permissionApi, userApi } from '../../api';
import './PermissionModal.css';

const PermissionModal = ({ visible, onClose, resourceType, resourceId, resourceTitle }) => {
    const [permissions, setPermissions] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Form State
    const [granteeType, setGranteeType] = useState('user'); // user / role
    const [granteeId, setGranteeId] = useState('');
    const [permissionType, setPermissionType] = useState('read');
    const [canGrant, setCanGrant] = useState(false);

    useEffect(() => {
        if (visible && resourceId) {
            fetchPermissions();
            fetchUsers();
        }
    }, [visible, resourceId]);

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const res = await permissionApi.list(resourceType, resourceId);
            if (res.code === 200) {
                setPermissions(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await userApi.getList({ page: 1, limit: 100 }); // Simple list for now
            if (res.code === 200) {
                setUsers(res.data.items);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleGrant = async () => {
        if (!granteeId) return alert('请选择授权对象');

        try {
            await permissionApi.grant({
                resource_type: resourceType,
                resource_id: resourceId,
                grantee_type: granteeType,
                grantee_id: granteeId,
                permission_type: permissionType,
                can_grant: canGrant
            });
            fetchPermissions();
            // Reset form slightly
            setCanGrant(false);
        } catch (error) {
            alert('授权失败: ' + error.message);
        }
    };

    const handleRevoke = async (permId) => {
        if (!window.confirm('确认撤销该权限？')) return;
        try {
            await permissionApi.revoke(permId);
            fetchPermissions();
        } catch (error) {
            alert('撤销失败: ' + error.message);
        }
    };

    if (!visible) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content permission-modal">
                <div className="modal-header">
                    <h3>权限管理 - {resourceTitle}</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">

                    {/* Add Permission Form */}
                    <div className="grant-form">
                        <h4>添加授权</h4>
                        <div className="form-group">
                            <label>对象类型:</label>
                            <select value={granteeType} onChange={e => setGranteeType(e.target.value)}>
                                <option value="user">用户</option>
                                <option value="role">角色</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>对象:</label>
                            {granteeType === 'user' ? (
                                <select value={granteeId} onChange={e => setGranteeId(e.target.value)}>
                                    <option value="">请选择用户</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.real_name || u.username}</option>
                                    ))}
                                </select>
                            ) : (
                                <select value={granteeId} onChange={e => setGranteeId(e.target.value)}>
                                    <option value="">请选择角色</option>
                                    <option value="admin">管理员</option>
                                    <option value="developer">开发者</option>
                                    <option value="sales">销售</option>
                                </select>
                            )}
                        </div>

                        <div className="form-group">
                            <label>权限:</label>
                            <select value={permissionType} onChange={e => setPermissionType(e.target.value)}>
                                <option value="read">只读</option>
                                <option value="write">读写</option>
                            </select>
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={canGrant}
                                    onChange={e => setCanGrant(e.target.checked)}
                                />
                                允许转授
                            </label>
                        </div>

                        <button className="grant-btn" onClick={handleGrant}>添加</button>
                    </div>

                    {/* Permission List */}
                    <div className="permission-list">
                        <h4>已有权限</h4>
                        {loading ? <p>加载中...</p> : (
                            <table>
                                <thead>
                                    <tr>
                                        <th>对象</th>
                                        <th>权限</th>
                                        <th>转授</th>
                                        <th>授权人</th>
                                        <th>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {permissions.map(p => {
                                        let granteeName = p.grantee_id;
                                        if (p.grantee_type === 'user') {
                                            const u = users.find(u => str(u.id) === p.grantee_id) || { real_name: 'Unknown' };
                                            // wait, users list might not filter all. ID lookup is best effort.
                                            // p.grantee_id is ID.
                                            // Just show ID if not found for MVP.
                                            // Actually users list is paginated, might miss. permission obj doesn't include grantee name unless I join in backend.
                                            // Backend Permission model doesn't join grantee.
                                            // I'll leave as ID or try to match.
                                            const matchedUser = users.find(u => String(u.id) === String(p.grantee_id));
                                            if (matchedUser) granteeName = matchedUser.real_name || matchedUser.username;
                                        }
                                        return (
                                            <tr key={p.id}>
                                                <td>{p.grantee_type === 'role' ? `角色: ${p.grantee_id}` : `用户: ${granteeName}`}</td>
                                                <td>{p.permission_type === 'write' ? '读写' : '只读'}</td>
                                                <td>{p.can_grant ? '是' : '否'}</td>
                                                <td>{p.created_by_name}</td>
                                                <td>
                                                    <button className="revoke-btn" onClick={() => handleRevoke(p.id)}>撤销</button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    {permissions.length === 0 && <tr><td colSpan="5" align="center">暂无特殊授权</td></tr>}
                                </tbody>
                            </table>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PermissionModal;

function str(v) { return String(v); }
